import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Bot, MessageCircle, Send, Save, RefreshCw,
    Settings, User, MapPin, Briefcase, MessageSquare,
    Clock, CheckCircle, AlertCircle, ShoppingBag, Plus, Trash2,
    ChevronRight, Layout, Phone, Globe, Sparkles, X, User2
} from 'lucide-react';
import { API_BASE_URL } from '../constants';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

const Card = ({ children, className = '' }: any) => (
    <div className={`bg-white rounded-3xl border border-surface-200 shadow-sm ${className}`}>
        {children}
    </div>
);

const PromptsTab = ({ tenantId }: { tenantId: string }) => {
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [activeSubTab, setActiveSubTab] = useState<'config' | 'simulator'>('config');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form States
    const [config, setConfig] = useState<any>({
        builder_ia_name: '',
        builder_niche: 'Varejo / E-commerce',
        builder_place_type: 'Loja Online',
        builder_specialty: '',
        builder_address: '',
        builder_reference: '',
        builder_tone_voice: 'Profissional e Empático',
        builder_response_length: 'short',
        builder_business_context: '',
        builder_services_json: [],
        builder_use_menu: false,
        builder_menu_config: '',
        builder_welcome_msg: 'Olá! Sou o assistente virtual da loja. Como posso te ajudar hoje?',
        trigger_mode: 'ALWAYS_ON',
        trigger_keywords: '',
        system_prompt: ''
    });

    // Simulator States
    const [chatMessage, setChatMessage] = useState('');
    const [chatHistory, setChatHistory] = useState<any[]>([]);
    const [isThinking, setIsThinking] = useState(false);
    const [promptSource, setPromptSource] = useState<'current' | 'saved'>('current');
    const chatEndRef = useRef<HTMLDivElement>(null);

    const loadData = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/tenant/${tenantId}`);
            const data = res.data;
            setConfig({
                builder_ia_name: data.builder_ia_name || '',
                builder_niche: data.builder_niche || 'Varejo / E-commerce',
                builder_place_type: data.builder_place_type || 'Loja Online',
                builder_specialty: data.builder_specialty || '',
                builder_address: data.builder_address || '',
                builder_reference: data.builder_reference || '',
                builder_tone_voice: data.builder_tone_voice || 'Profissional e Empático',
                builder_response_length: data.builder_response_length || 'short',
                builder_business_context: data.builder_business_context || '',
                builder_services_json: Array.isArray(data.builder_services_json) ? data.builder_services_json : [],
                builder_use_menu: data.builder_use_menu || false,
                builder_menu_config: data.builder_menu_config || '',
                builder_welcome_msg: data.builder_welcome_msg || 'Olá! Como posso te ajudar hoje?',
                trigger_mode: data.ai_config?.trigger_mode || 'ALWAYS_ON',
                trigger_keywords: data.ai_config?.trigger_keywords || '',
                system_prompt: data.system_prompt || data.ai_config?.system_prompt || ''
            });
        } catch (err) {
            console.error('Erro ao carregar configurações da IA', err);
            showToast('Erro ao carregar dados da IA', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [tenantId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, isThinking]);

    const handleSave = async () => {
        const confirmed = await confirm({
            title: 'Atenção: Alteração de Prompt',
            message: 'Alterar as configurações da IA irá gerar um novo prompt de sistema. Caso o bot apresente comportamentos inesperados após a alteração, poderá haver cobrança de taxa de manutenção para correção manual por nossos especialistas. Deseja continuar?',
            type: 'warning'
        });

        if (!confirmed) return;

        setSaving(true);
        try {
            await axios.post(`${API_BASE_URL}/admin/tenant/${tenantId}/generate-prompt`, config);
            showToast('Configurações salvas e Prompt atualizado!', 'success');
            loadData();
        } catch (err) {
            showToast('Erro ao salvar configurações', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSendMessage = async (e: any) => {
        e.preventDefault();
        if (!chatMessage.trim() || isThinking) return;

        const userMsg = { role: 'user', content: chatMessage };
        setChatHistory((prev: any[]) => [...prev, userMsg]);
        setChatMessage('');
        setIsThinking(true);

        try {
            // Se for "current", precisamos gerar o prompt temporário no frontend para testar antes de salvar
            // Mas para simplificar e garantir precisão, o backend já tem essa lógica.
            // Aqui vamos simular o que o usuário está testando.

            const res = await axios.post(`${API_BASE_URL}/test-prompt`, {
                prompt: config.system_prompt,
                message: chatMessage,

                history: chatHistory.slice(-4)
            });

            setChatHistory((prev: any[]) => [...prev, { role: 'assistant', content: res.data.reply }]);
        } catch (err: any) {
            showToast(err.response?.data?.error || 'Erro ao processar mensagem', 'error');
        } finally {
            setIsThinking(false);
        }
    };

    const assemblePreviewPrompt = (data: any) => {
        const {
            builder_ia_name, builder_niche, builder_place_type, builder_specialty,
            builder_address, builder_reference, builder_tone_voice, builder_response_length,
            builder_business_context, builder_services_json, builder_use_menu, builder_menu_config, builder_welcome_msg
        } = data;

        let prompt = `### [IDENTIDADE & MISSÃO]\n\n`;
        prompt += `Você é o assistente virtual "${builder_ia_name || 'Consultor'}" do(a) ${builder_place_type || 'estabelecimento'}. `;
        prompt += `Atuamos no nicho de ${builder_niche || 'atendimento e vendas'}. `;
        if (builder_specialty) prompt += `Nossa especialidade é ${builder_specialty}. `;

        if (builder_address) prompt += `Estamos localizados em: ${builder_address}. `;
        if (builder_reference) prompt += `Ponto de referência: ${builder_reference}. `;

        prompt += `\n\nSua missão é atender, tirar dúvidas e conduzir o cliente até a finalização de compra de forma natural, cordial e objetiva. `;
        prompt += `Seu tom de voz deve ser ${builder_tone_voice || 'profissional e prestativo'}. `;
        prompt += `Suas respostas devem ser ${builder_response_length === 'long' ? 'detalhadas e explicativas' : 'curtas e diretas'}. `;

        prompt += `\n\n---`;
        prompt += `\n\n### [REGRA DE OURO — ZERO ALUCINAÇÃO]\n\n`;
        prompt += `JAMAIS invente informações sobre o local, preços, serviços ou estoque. Se não souber algo, informe que irá consultar um atendente humano.`;

        prompt += `\n\n---`;
        prompt += `\n\n### [COMANDOS TÉCNICOS DO SISTEMA]\n\n`;
        prompt += `Para consultar estoque ou buscar um produto, gere EXATAMENTE:\n|||BUSCA_PRODUTO|||\nTERMO: <termo da busca>\n|||FIM_BUSCA|||\n\n`;
        prompt += `Para consultar os pedidos do cliente, gere APENAS:\n|||MEUS_PEDIDOS|||\n\n`;
        prompt += `Para transferir para um atendente humano (quando o cliente pedir ou a situação exigir), gere APENAS:\n|||TRANSBORDO|||`;

        if (builder_business_context) {
            prompt += `\n\n---`;
            prompt += `\n\n### [REGRAS DO NEGÓCIO & CONTEXTO]\n\n${builder_business_context}`;
        }

        const services = Array.isArray(builder_services_json) ? builder_services_json : [];
        if (services.length > 0) {
            prompt += `\n\n---`;
            prompt += `\n\n### [CATÁLOGO/SERVIÇOS & PREÇOS]\n\n`;
            services.forEach((s: any) => {
                prompt += `- ${s.name}: R$ ${s.price}${s.description ? ` (${s.description})` : ''}\n`;
            });
        }

        if (builder_use_menu && builder_menu_config) {
            prompt += `\n\n---`;
            prompt += `\n\n### [MENU INICIAL]\n\nQuando o cliente enviar uma saudação inicial ou pedir o menu, responda:\n\n"${builder_menu_config}"`;
        } else if (builder_welcome_msg) {
            prompt += `\n\n---`;
            prompt += `\n\n### [SAUDAÇÃO]\n\nSua mensagem de boas-vindas: "${builder_welcome_msg}"`;
        }

        prompt += `\n\n---`;
        prompt += `\n\n### [DIRETRIZES FINAIS]\n\n`;
        prompt += `1. Use gatilhos mentais com elegância (escassez, autoridade, prova social) quando apropriado.\n`;
        prompt += `2. Nunca pressione o cliente.\n`;
        prompt += `3. Toda a jornada de compra acontece aqui, conduza o cliente para os links gerados pelo sistema.\n`;

        return prompt;
    };

    const addService = () => {
        setConfig((prev: any) => ({
            ...prev,
            builder_services_json: [...prev.builder_services_json, { name: '', price: '', description: '' }]
        }));
    };

    const updateService = (index: number, field: string, value: string) => {
        const newServices = [...config.builder_services_json];
        newServices[index] = { ...newServices[index], [field]: value };
        setConfig((prev: any) => ({ ...prev, builder_services_json: newServices }));
    };

    const removeService = (index: number) => {
        setConfig((prev: any) => ({
            ...prev,
            builder_services_json: prev.builder_services_json.filter((_: any, i: number) => i !== index)
        }));
    };

    if (loading) return <div className="p-12 text-center text-gray-400 animate-pulse">Carregando cérebro da IA...</div>;

    return (
        <div className="space-y-6 animate-fade-in max-w-5xl mx-auto pb-10">
            {/* Header Tabs Navigation */}
            <div className="flex p-1 bg-surface-100 rounded-2xl w-fit mx-auto mb-8 shadow-inner">
                <button
                    onClick={() => setActiveSubTab('config')}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all duration-200 text-sm font-bold ${activeSubTab === 'config' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    <Settings size={18} /> Configuração & Prompt
                </button>
                <button
                    onClick={() => setActiveSubTab('simulator')}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all duration-200 text-sm font-bold ${activeSubTab === 'simulator' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    <MessageSquare size={18} /> Simulador de Fluxo
                </button>
            </div>

            {activeSubTab === 'config' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-3 bg-brand-50 text-brand-600 rounded-2xl">
                                    <Bot size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-surface-900 leading-none">Assistente de Configuração da IA</h3>
                                    <p className="text-xs text-gray-400 mt-1">Configure o comportamento, regras e serviços do seu robô inteligente.</p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                {/* Seção: Gatilho de Disparo */}
                                <div className="bg-brand-50/50 p-6 rounded-2xl border border-brand-100">
                                    <div className="flex items-center gap-2 mb-4 text-brand-700">
                                        <Phone size={16} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Modo de Ativação</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-600 ml-1">Quando a IA deve responder?</label>
                                            <select
                                                className="input-primary w-full p-3 rounded-xl border border-gray-200 bg-white focus:bg-white transition-all outline-none appearance-none font-bold text-brand-900"
                                                value={config.trigger_mode}
                                                onChange={e => setConfig({ ...config, trigger_mode: e.target.value })}
                                            >
                                                <option value="ALWAYS_ON">Sempre (Todas as msgs)</option>
                                                <option value="KEYWORD">Apenas por Palavra-Chave</option>
                                            </select>
                                        </div>
                                        {config.trigger_mode === 'KEYWORD' && (
                                            <div className="space-y-1 animate-in fade-in slide-in-from-top-2">
                                                <label className="text-xs font-bold text-gray-600 ml-1">Palavras-Chave (separadas por vírgula)</label>
                                                <input
                                                    className="input-primary w-full p-3 rounded-xl border border-gray-200 bg-white focus:bg-white transition-all outline-none font-bold"
                                                    value={config.trigger_keywords}
                                                    onChange={e => setConfig({ ...config, trigger_keywords: e.target.value })}
                                                    placeholder="Ex: oi, ajuda, preco"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Seção: Identidade */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 text-brand-600">
                                        <User size={16} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Identidade Básica</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Qual o nome do seu robô?</label>
                                            <input
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none"
                                                value={config.builder_ia_name}
                                                onChange={e => setConfig({ ...config, builder_ia_name: e.target.value })}
                                                placeholder="Ex: Vendedor Digital"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Qual seu nicho?</label>
                                            <select
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none appearance-none"
                                                value={config.builder_niche}
                                                onChange={e => setConfig({ ...config, builder_niche: e.target.value })}
                                            >
                                                <option>Estética / Saúde</option>
                                                <option>Varejo / E-commerce</option>
                                                <option>Serviços Técnicos</option>
                                                <option>Gastronomia</option>
                                                <option>Imobiliária</option>
                                                <option>Consultoria</option>
                                                <option>Geral / Serviços / Outros</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Como você chama o local?</label>
                                            <select
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none appearance-none"
                                                value={config.builder_place_type}
                                                onChange={e => setConfig({ ...config, builder_place_type: e.target.value })}
                                            >
                                                <option>Loja Física</option>
                                                <option>Loja Online / E-commerce</option>
                                                <option>Farmácia</option>
                                                <option>Restaurante / Delivery</option>
                                                <option>Corretor / Imobiliária</option>
                                                <option>Empresa</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Qual sua especialidade?</label>
                                            <input
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none"
                                                value={config.builder_specialty}
                                                onChange={e => setConfig({ ...config, builder_specialty: e.target.value })}
                                                placeholder="Ex: Pilates, Manicure, Direito Penal"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Seção: Comportamento */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 text-brand-600">
                                        <Sparkles size={16} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Contexto & Comportamento</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Tom de Voz</label>
                                            <select
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none appearance-none"
                                                value={config.builder_tone_voice}
                                                onChange={e => setConfig({ ...config, builder_tone_voice: e.target.value })}
                                            >
                                                <option>Profissional e Neutro</option>
                                                <option>Descontraído e Amigável</option>
                                                <option>Formal e Respeitoso</option>
                                                <option>Empático e Acolhedor</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Tamanho da Resposta</label>
                                            <select
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none appearance-none"
                                                value={config.builder_response_length}
                                                onChange={e => setConfig({ ...config, builder_response_length: e.target.value })}
                                            >
                                                <option value="short">Respostas Curtas e Diretas</option>
                                                <option value="long">Respostas Detalhadas</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 ml-1">Como o seu negócio funciona?</label>
                                        <textarea
                                            rows={4}
                                            className="input-primary w-full p-4 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none resize-none"
                                            value={config.builder_business_context}
                                            onChange={e => setConfig({ ...config, builder_business_context: e.target.value })}
                                            placeholder="Ex: Somos uma loja online de roupas. Oferecemos frete grátis para compras acima de R$ 200. Pagamento via Pix tem 5% de desconto. O prazo de entrega é de até 5 dias úteis."
                                        />
                                    </div>
                                </div>

                                {/* Seção: Localização */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 text-brand-600">
                                        <MapPin size={16} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Localização</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Endereço Completo</label>
                                            <input
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none"
                                                value={config.builder_address}
                                                onChange={e => setConfig({ ...config, builder_address: e.target.value })}
                                                placeholder="Rua Exemplo, 123"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 ml-1">Ponto de Referência</label>
                                            <input
                                                className="input-primary w-full p-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none"
                                                value={config.builder_reference}
                                                onChange={e => setConfig({ ...config, builder_reference: e.target.value })}
                                                placeholder="Próximo ao..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Seção: Menu Inteligente */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 text-brand-600">
                                        <MessageSquare size={16} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Menu de Atendimento</span>
                                    </div>
                                    <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 w-4 h-4"
                                                checked={config.builder_use_menu}
                                                onChange={e => setConfig({ ...config, builder_use_menu: e.target.checked })}
                                            />
                                            <span className="text-sm font-bold text-gray-700">Ativar Menu Inicial</span>
                                        </label>

                                        {config.builder_use_menu && (
                                            <div className="space-y-1 animate-in fade-in slide-in-from-top-2 pt-2">
                                                <label className="text-xs font-bold text-gray-500 ml-1">Opções do Menu (Crie sua lista customizada)</label>
                                                <textarea
                                                    rows={5}
                                                    className="input-primary w-full p-4 rounded-xl border border-gray-200 bg-white focus:bg-white transition-all outline-none resize-none font-mono text-xs"
                                                    value={config.builder_menu_config}
                                                    onChange={e => setConfig({ ...config, builder_menu_config: e.target.value })}
                                                    placeholder="Ex:&#10;1. 🛍️ Ver Catálogo e Ofertas&#10;2. 📦 Meus Pedidos&#10;3. 👤 Falar com Atendente"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Boas-Vindas */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase text-brand-600 tracking-widest ml-1 block mb-2">Mensagem de Boas-Vindas</label>
                                    <input
                                        className="input-primary w-full p-4 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white transition-all outline-none"
                                        value={config.builder_welcome_msg}
                                        onChange={e => setConfig({ ...config, builder_welcome_msg: e.target.value })}
                                        placeholder="Olá! Como posso te ajudar hoje?"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full mt-10 bg-gradient-to-r from-[#003B21] to-[#00542F] hover:from-[#002B18] hover:to-[#004225] text-white font-black py-4 rounded-2xl shadow-xl shadow-brand-500/20 transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
                            >
                                {saving ? <RefreshCw className="animate-spin" size={20} /> : <Sparkles size={20} />}
                                {saving ? 'Compilando Cérebro...' : 'Confirmar e Gerar Prompt'}
                            </button>
                        </Card>
                    </div>

                    <div className="lg:col-span-1 space-y-6">
                        <Card className="p-6 bg-brand-50/30 border-brand-100">
                            <h4 className="font-black text-brand-800 flex items-center gap-2 mb-3">
                                <AlertCircle size={20} /> Dicas da IA
                            </h4>
                            <ul className="space-y-3 text-xs text-brand-900/70 font-medium">
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1 flex-shrink-0" /> Seja específico sobre o nicho para respostas mais precisas.</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1 flex-shrink-0" /> No contexto do negócio, insira regras como horários ou links importantes.</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1 flex-shrink-0" /> Teste sempre no simulador antes de aplicar na conexão real.</li>
                            </ul>
                        </Card>

                        <Card className="p-6">
                            <h4 className="font-black text-surface-900 flex items-center gap-2 mb-4">
                                <Layout size={20} className="text-gray-400" /> Preview do Prompt
                            </h4>
                            <div className="bg-gray-50 rounded-2xl p-4 max-h-96 overflow-y-auto text-[10px] font-mono whitespace-pre-wrap text-gray-500 leading-relaxed custom-scrollbar">
                                {config.system_prompt || 'Nenhum prompt salvo ainda. Gere o prompt abaixo.'}
                            </div>
                        </Card>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Disclaimer / Sobre o Simulador */}
                    <div className="bg-yellow-50 p-5 rounded-2xl border border-yellow-200 shadow-sm mx-auto max-w-2xl">
                        <h5 className="text-sm font-black text-yellow-800 mb-2 flex items-center gap-2">
                            <AlertCircle size={16} /> AVISO: SOBRE ESTE SIMULADOR
                        </h5>
                        <p className="text-sm text-yellow-700 leading-relaxed">
                            Esta é uma simulação interna para validar a lógica e o comportamento de comunicação da sua IA.
                            <strong> Não reflete integrações sistêmicas do seu n8n real. </strong>
                            Buscas de produtos, integrações com checkout e estoques reais não acontecem até o prompt estar ativo na plataforma principal.
                        </p>
                    </div>

                    {/* SIMULADOR DE CHAT (WHATSAPP STYLE) */}
                    <div className="max-w-md mx-auto h-[600px] flex flex-col bg-[#E5DDD5] rounded-[2.5rem] border-[8px] border-surface-900 shadow-2xl relative overflow-hidden">
                        {/* Header do WhatsApp */}
                        <div className="bg-[#075E54] p-4 flex items-center gap-3 text-white">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                <Bot size={24} />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-bold leading-none">{config.builder_ia_name || 'IA Atendente'}</h4>
                                <span className="text-[10px] opacity-70">online</span>
                            </div>
                            <div className="flex gap-4">
                                <Phone size={18} />
                                <Settings size={18} />
                            </div>
                        </div>

                        {/* Status do Simulador */}
                        <div className="absolute top-16 left-0 right-0 p-2 bg-white/80 backdrop-blur-md flex flex-col items-center gap-1 z-10 border-b border-gray-100">
                            <span className="text-[10px] px-3 py-1 rounded-full font-black bg-brand-600 text-white shadow-md shadow-brand-200">
                                Simulando Prompt Salvo
                            </span>
                            <p className="text-[8px] text-gray-400 font-medium px-4 text-center leading-tight">
                                Este simulador usa o modelo {config.model || 'GPT-4o Mini'} com o prompt configurado. Respostas podem variar da conexão real.
                            </p>
                        </div>

                        {/* Corpo das Mensagens */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar pt-20 pb-4">
                            {chatHistory.length === 0 && (
                                <div className="text-center py-10 opacity-50 px-6 mt-10">
                                    <p className="text-xs text-gray-600 italic">Este é o ambiente de testes. Envie uma mensagem para ver como a IA se comporta com suas novas configurações.</p>
                                </div>
                            )}
                            {chatHistory.map((m, idx) => (
                                <div
                                    key={idx}
                                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`max-w-[85%] p-2.5 rounded-xl text-sm shadow-sm relative whitespace-pre-wrap ${m.role === 'user'
                                        ? 'bg-[#DCF8C6] text-gray-800 rounded-tr-none'
                                        : 'bg-white text-gray-800 rounded-tl-none'
                                        }`}>
                                        {m.content}
                                        <div className="text-[9px] opacity-40 text-right mt-1">
                                            {new Date().getHours()}:{new Date().getMinutes().toString().padStart(2, '0')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isThinking && (
                                <div className="flex justify-start pt-2">
                                    <div className="bg-white p-3 rounded-xl shadow-sm rounded-tl-none flex gap-1 items-center">
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Input do WhatsApp */}
                        <form onSubmit={handleSendMessage} className="p-3 bg-white flex gap-2 items-center">
                            <input
                                className="flex-1 bg-gray-100 p-2.5 rounded-full text-xs outline-none border border-transparent focus:border-brand-500 transition-all"
                                placeholder="Digite uma mensagem..."
                                value={chatMessage}
                                onChange={e => setChatMessage(e.target.value)}
                            />
                            <button
                                type="submit"
                                disabled={!chatMessage.trim() || isThinking}
                                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${chatMessage.trim() && !isThinking ? 'bg-[#008069] text-white shadow-lg scale-105' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                                <Send size={18} />
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromptsTab;
